import React, { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { classes } from "components/Utility";

export interface TooltipProviderValue {
  show: (text: ReactNode, width?: number, className?: string) => void,
  hide: () => void,
}

export const TooltipContext = React.createContext<TooltipProviderValue>(null);

interface Props {
    children: ReactNode,
    document?: Document,
}

/** Gets a ref to be passed to a react element to make a tooltip
 *  IMPORTANT NOTE: do not call this inline, wrap in useMemo for functional components or make it an instance variable for class components
*/
export const getTooltipRef = (tooltipMessage: ReactNode, tooltipContext: TooltipProviderValue, document: Document = window.document) => {
  const showTooltip = () => tooltipContext.show(tooltipMessage);
  const hideTooltip = () => tooltipContext.hide();
  return (element: HTMLElement) => {
    if (element) {
      element.addEventListener("mouseover", showTooltip);
      element.addEventListener("mouseout", hideTooltip);
    }
    else {
      hideTooltip();
    }
  };
};

const setTooltipPos = (tooltip: HTMLDivElement, x: number, y: number) => {
  let padding = 10;
  if (tooltip && x && y) {
    if(x + tooltip.offsetWidth > document.body.offsetWidth - padding * 2)
      tooltip.style.left = (x - padding - tooltip.offsetWidth) + "px";
    else
      tooltip.style.left = (x + padding) + "px";

    if(y + tooltip.offsetHeight > document.body.offsetHeight - padding * 2)
      tooltip.style.top = (y - padding - tooltip.offsetHeight) + "px";
    else
      tooltip.style.top = (y + padding) + "px";
  }
};

const TooltipProvider = ({ children, document = window.document }: Props): ReactElement => {
  const [isShowing, setIsShowing] = useState<boolean>(false);
  const [contents, setContents] = useState<ReactNode>(null);
  const [className, setClassName] = useState<string>(null);
  const [width, setWidth] = useState<number>(null);
  const instance = useRef<{ tooltipElement?: HTMLDivElement, mousePos?: { x: number, y: number }}>({});

  const mouseListener = useCallback((evt: MouseEvent) => {
    // need to save the mouse position because you can get the tooltip to glitch up sometimes if it shows but a mouse move event wasnt fired, so the tooltips position wont be updated
    instance.current.mousePos = { x: evt.clientX, y: evt.clientY };
    setTooltipPos(instance.current.tooltipElement, evt.clientX, evt.clientY);
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", mouseListener);

    return () => {
      document.removeEventListener("mousemove", mouseListener);
    };
  }, [mouseListener]);

  const show: TooltipProviderValue["show"] = (text, width = null, className = null) => {
    setContents(text);
    setClassName(className);
    setWidth(width);
    setIsShowing(true);
  };

  const hide: TooltipProviderValue["hide"] = () => {
    setContents(null);
    setClassName(null);
    setWidth(null);
    setIsShowing(false);
  };

  const providerVal = useRef<TooltipProviderValue>({
    show,
    hide,
  });

  let style: React.CSSProperties = {
    zIndex: 9999,
    position: 'fixed',
    border: '1px var(--tooltip-border-color) solid',
    backgroundColor: 'var(--tooltip-bg-color)',
    padding: 'var(--padding-5px)',
  };

  if (width) {
    style.width = width;
  }

  const updateTooltipRef = (element: HTMLDivElement) => {
    instance.current.tooltipElement = element;

    setTooltipPos(element, instance.current.mousePos?.x, instance.current.mousePos?.y);
  };

  return (
    <TooltipContext.Provider value={providerVal.current}>
      {isShowing && <div style={style} className={className} ref={updateTooltipRef}>{contents}</div>}
      {children}
    </TooltipContext.Provider>
  );
};

export default TooltipProvider;
